'use client';

import React, { useEffect, useState } from 'react';
import {
  Sparkles, CheckCircle2, AlertCircle, MessageSquare, CalendarClock, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useActivityDigest, DigestEvent } from '@/hooks/useActivityDigest';
import BranchTag from '@/components/ui/BranchTag';
import Icon from '@/components/ui/AppIcon';


type Branch = 'live_events' | 'studio' | 'outreach' | 'internal';

const EVENT_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string; label: string }> = {
  new_urgent:       { icon: AlertCircle,    color: 'text-red-500',    bg: 'bg-red-50',    label: 'New urgent item' },
  time_off_approved:{ icon: CheckCircle2,   color: 'text-emerald-500',bg: 'bg-emerald-50',label: 'Time off approved' },
  gap_resolved:     { icon: CheckCircle2,   color: 'text-teal-500',   bg: 'bg-teal-50',   label: 'Gap resolved' },
  new_message:      { icon: MessageSquare,  color: 'text-indigo-500', bg: 'bg-indigo-50', label: 'New message' },
  schedule_change:  { icon: CalendarClock,  color: 'text-amber-500',  bg: 'bg-amber-50',  label: 'Schedule change' },
};

function getConfig(eventType: string) {
  return EVENT_CONFIG[eventType] ?? { icon: Sparkles, color: 'text-slate-400', bg: 'bg-slate-50', label: eventType };
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function ActivityDigest() {
  const { events, lastSeenAt, loading, markSeen } = useActivityDigest();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Mark seen after 3 seconds of viewing
  useEffect(() => {
    if (!loading && events.length > 0) {
      const t = setTimeout(() => markSeen(), 3000);
      return () => clearTimeout(t);
    }
  }, [loading, events.length, markSeen]);

  if (dismissed || loading) return null;
  if (events.length === 0) return null;

  const PREVIEW_COUNT = 3;
  const visible = expanded ? events : events.slice(0, PREVIEW_COUNT);

  return (
    <div className="bg-white rounded-xl border border-indigo-100 shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-indigo-50 flex items-center gap-2 bg-indigo-50/60">
        <Sparkles size={14} className="text-indigo-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-semibold text-slate-800">Since you were last here</h3>
          {lastSeenAt && (
            <p className="text-[11px] text-slate-400">
              Last visit: {new Date(lastSeenAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{' '}
              at {new Date(lastSeenAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </p>
          )}
        </div>
        <span className="text-[11px] font-semibold bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full shrink-0">
          {events.length} update{events.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded-md hover:bg-indigo-100 text-slate-400 hover:text-slate-600 transition-colors shrink-0"
          aria-label="Dismiss digest"
        >
          <X size={13} />
        </button>
      </div>

      {/* Event list */}
      <div className="divide-y divide-stone-50">
        {visible.map((event) => (
          <DigestRow key={event.id} event={event} />
        ))}
      </div>

      {/* Expand / collapse */}
      {events.length > PREVIEW_COUNT && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-2.5 flex items-center justify-center gap-1.5 text-[12px] font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50/40 transition-colors border-t border-stone-50"
        >
          {expanded ? (
            <><ChevronUp size={12} /> Show less</>
          ) : (
            <><ChevronDown size={12} /> Show {events.length - PREVIEW_COUNT} more</>
          )}
        </button>
      )}
    </div>
  );
}

function DigestRow({ event }: { event: DigestEvent }) {
  const cfg = getConfig(event.event_type);
  const Icon = cfg.icon;

  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <div className={`w-7 h-7 rounded-full ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
        <Icon size={13} className={cfg.color} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-slate-800 leading-snug">{event.title}</p>
        {event.detail && (
          <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{event.detail}</p>
        )}
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {event.branch && (
            <BranchTag branch={event.branch as Branch} size="xs" />
          )}
          {event.actor_name && (
            <span className="text-[10px] text-slate-400">{event.actor_name}</span>
          )}
          <span className="text-[10px] text-slate-300">{timeAgo(event.created_at)}</span>
        </div>
      </div>
    </div>
  );
}
